import React, { useEffect, useState } from "react";
import {
  Box,
  Modal,
  Typography,
  TextField,
  Button,
  Stack,
  IconButton,
  FormHelperText,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { addOrUpdateExpense } from "../../api/expense";
import { useNotification } from "../../hooks/useNotification";

const formatDateValue = (date)=>{
  return  new Date(date || new Date()).toISOString()?.split("T")?.[0]
}

const AddExpenseModal = ({ open, handleClose, mode,selectedExpense,onSuccess }) => {
  const formInitialValue = { name: "", description: "", amount: "", date: formatDateValue() }
  const [formData, setFormData] = useState(formInitialValue)
    const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const { showErrorNotification, showSuccessNotification } = useNotification();

  const handleOnChange = (name, value) => {
    setFormData({ ...formData, [name]: value })
  }

  const validate = (f) => {
    const e = {};
    if (!f?.name?.trim()) e.name = "Name is required";

    if (!f?.description?.trim()) e.description = "Description is required";

    if (!f?.date) e.date = "Date is required";

    if (!f?.amount) e.amount = "Amount is required";

    return e;
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    const e = validate(formData);
    setErrors(e);
    if (Object.keys(e).length) return;
    const isEditMode = mode !== "add"
    setLoading(true)
    addOrUpdateExpense(formData,isEditMode)
      .then(res => {
        showSuccessNotification(`Successfully ${isEditMode ?  "update" : "added"} expense`)
        setFormData(formInitialValue)
        handleClose()
        onSuccess && onSuccess()
      }).catch(err => showErrorNotification(err?.message || `Failed to ${isEditMode ? "update" : "add"  } expense`))
      .finally(result => {
        setLoading(false)
      })
  };

    const handleOnBlur = () => {
    if (Object.keys(errors)?.length) {
      const e = validate(formData);
      setErrors(e);
      if (Object.keys(e).length) return;
    }
  }

  useEffect(()=>{
    if(selectedExpense && typeof selectedExpense==="object"){
      setFormData({...selectedExpense,date:formatDateValue(selectedExpense?.date)})
    }else{
      setFormData(formInitialValue)
    }
    setErrors({})
  },[selectedExpense])
  

  return (
    <Modal open={open} onClose={handleClose}>
      <Box
        sx={{
          width: 480,
          bgcolor: "#fff",
          borderRadius: 2,
          boxShadow: 4,
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          maxHeight: "90vh",
          display: "flex",
          flexDirection: "column",
          p: 0,
        }} >
        {/* ===== Fixed Header ===== */}
        <Box
          sx={{
            position: "sticky",
            top: 0,
            bgcolor: "#fff",
            zIndex: 10,
            px: 3,
            py: 2,
            borderBottom: "1px solid #eee",
          }}
        >
          <IconButton
            onClick={handleClose}
            sx={{
              position: "absolute",
              top: 16,
              right: 16,
              color: "#000",
            }}
          >
            <CloseIcon />
          </IconButton>

          <Typography variant="h6" fontWeight={700}>
            {mode === "add" ? "Add New Expense" : "Edit Expense"}
          </Typography>

          <Typography
            sx={{
              fontSize: "14px",
              fontWeight: 400,
              color: "#717182",
              mt: 0,
            }}
          >
            Fill in the details below to add a new expense to your records.
          </Typography>
        </Box>

        {/* ===== Scrollable Body ===== */}
        <Box
          sx={{
            p: 3,
            pt: 2,
            overflowY: "auto",
            flexGrow: 1,
          }}
        >
          <Stack spacing={2}>
            {/* Expense Name */}
            <Box>
              <Typography fontSize="14px" fontWeight={500} mb={0.5}>
                Expense Name
              </Typography>
              <TextField
                placeholder="e.g., Cloud Hosting Service"
                name="name"
                value={formData?.name}
                 onBlur={handleOnBlur}
                onChange={(e) => { handleOnChange("name", e?.target?.value) }}
                fullWidth
                sx={{
                  "& .MuiInputBase-root": {
                    height: "40px",
                  },
                  "& input": {
                    fontSize: '14px'
                  },
                }}
              />
               {errors.name && <FormHelperText error>{errors.name}</FormHelperText>}
            </Box>

            {/* Description */}
            <Box>
              <Typography fontSize="14px" fontWeight={500} mb={0.5}>
                Description
              </Typography>
              <TextField
                placeholder="Enter expense description..."
                name="description"
                value={formData?.description}
                onBlur={handleOnBlur}
                onChange={(e) => { handleOnChange("description", e?.target?.value) }}
                multiline
                rows={3}
                fullWidth
                sx={{
                  "& textarea": {
                    fontSize: '14px'
                  },
                  "& textarea::placeholder": {
                    fontSize: "14px !important",    // optional
                  },
                }}
              />
               {errors.description && <FormHelperText error>{errors.description}</FormHelperText>}

            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
              {/* Date */}
              <Box sx={{ width: '100%' }}>
                <Typography fontSize="14px" fontWeight={500} mb={0.5}>
                  Date
                </Typography>
                <TextField
                  type="date"
                  fullWidth
                  name="date"
                  value={formData?.date}
                   onBlur={handleOnBlur}
                  onChange={(e) => { handleOnChange("date", e?.target?.value) }}
                  InputLabelProps={{ shrink: true, }}
                  inputProps={{ max: formatDateValue()}}
                  sx={{
                    "& .MuiInputBase-root": {
                      height: "40px",
                    },
                    "& input": {
                      fontSize: '14px'
                    },
                  }}
                />
               {errors.date && <FormHelperText error>{errors.date}</FormHelperText>}

              </Box>

              {/* Amount */}
              <Box sx={{ width: '100%' }}>
                <Typography fontSize="14px" fontWeight={500} mb={0.5}>
                  Amount (₹)
                </Typography>
                <TextField
                  type="text"
                  placeholder="0.00"
                  name="amount"
                  value={formData?.amount || ""}
                   onBlur={handleOnBlur}
                  onChange={(e) => {
                    const value = e.target.value;
                    // console.log("value",value);
                    
                    if (value === "") {
                      handleOnChange("amount", value);
                      return;
                    }
                    // allow only positive numbers (no - , no special chars)
                    const isValid = /^\d+(\.\d*)?$/.test(value);
                    if (isValid) {
                      handleOnChange("amount", value);
                    }
                  }}

                  fullWidth
                  sx={{
                    "& .MuiInputBase-root": {
                      height: "40px",
                    },
                    // Chrome, Safari, Edge ke liye
                    "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button": {
                      display: "none",
                    },
                    // Firefox ke liye
                    "& input[type=number]": {
                      MozAppearance: "textfield",
                    },
                    "& input": {
                      fontSize: '14px'
                    },
                  }}
                />
               {errors.amount && <FormHelperText error>{errors.amount}</FormHelperText>}

              </Box>
            </Box>
          </Stack>
        </Box>

        {/* ===== Footer Buttons (Fixed) ===== */}
        <Box
          sx={{
            p: 2,
            borderTop: "1px solid #eee",
            display: "flex",
            justifyContent: "flex-end",
            gap: 2,
            bgcolor: "#fff",
          }}
        >
          <Button
            onClick={handleClose}
            sx={{
              textTransform: "capitalize",
              fontWeight: 400,
              px: 3, border: '1px solid #E0E2E9', fontSize: '16px', color: '#3E3E3E'
            }}
          >
            Cancel
          </Button>

          <Button
          disabled={loading}
          onClick={handleSubmit}
            variant="contained"
            sx={{ background: "#BE1F1F", fontSize: "16px", px: 2, fontWeight: "400", color: "#fff", textTransform: "capitalize", borderRadius: 1, '&:hover': { background: '#FF4A4A' } }}
          >
            {mode === "add" ? "Add Expense" : "Update"}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default AddExpenseModal;
